import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Timestamp } from "firebase/firestore";
import { RootState, AppDispatch } from "../../store/store";
import {
  sendMessage,
  uploadFile,
  setActiveChat,
  clearChat,
  startChatListener,
  stopChatListener,
  Message,
} from "../../store/chatSlice";
import Camera from "../../Images/camera-small.png";
import Documents from "../../Images/Documents.png";
//import Camera from "./Images/camera-small.png";
//import Documents from "./Images/Documents.png";

interface ChatProps {
  senderEmail: string;
  receiverEmail: string;
  receiverId: string;
  receiverDisplayName: string;
  onClose?: () => void;
}

const Chat: React.FC<ChatProps> = ({
  senderEmail,
  receiverEmail,
  receiverId,
  receiverDisplayName,
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { messages, loading, error } = useSelector(
    (state: RootState) => state.chat
  );

  const [newMessage, setNewMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Load chat history when component mounts or when chat participants change
  useEffect(() => {
    dispatch(setActiveChat({ receiverEmail, receiverId }));

    // Start real-time listener for this chat
    startChatListener(senderEmail, receiverEmail, dispatch);

    return () => {
      // Clean up listener when component unmounts or chat changes
      stopChatListener();
      dispatch(clearChat());
    };
  }, [dispatch, senderEmail, receiverEmail, receiverId]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !selectedFile) || loading) return;

    try {
      if (selectedFile) {
        // Handle file upload
        await dispatch(
          uploadFile({
            file: selectedFile,
            senderEmail,
            receiverEmail,
          })
        ).unwrap();
      } else {
        // Handle text message
        await dispatch(
          sendMessage({
            senderEmail,
            receiverEmail,
            text: newMessage.trim(),
            type: "text",
          })
        ).unwrap();
      }

      setNewMessage("");
      setSelectedFile(null);
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert("File size must be less than 10MB");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.size > 10 * 1024 * 1024) {
        alert("File size must be less than 10MB");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const formatTime = (timestamp: Timestamp | Date) => {
    // Convert Firestore Timestamp to Date if needed
    const date = timestamp instanceof Date ? timestamp : timestamp.toDate();
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (timestamp: Timestamp | Date) => {
    const today = new Date();
    // Convert Firestore Timestamp to Date if needed
    const messageDate =
      timestamp instanceof Date ? timestamp : timestamp.toDate();

    if (messageDate.toDateString() === today.toDateString()) {
      return "Today";
    }

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (messageDate.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    }

    return messageDate.toLocaleDateString();
  };

  const renderMessage = (message: Message, index: number) => {
    const isOwnMessage = message.senderEmail === senderEmail;
    const showDate =
      index === 0 ||
      formatDate(messages[index - 1].timestamp) !==
        formatDate(message.timestamp);

    return (
      <div key={message.id}>
        {showDate && (
          <div
            style={{
              textAlign: "center",
              margin: "16px 0",
              color: "#65676b",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            {formatDate(message.timestamp)}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: isOwnMessage ? "flex-end" : "flex-start",
            marginBottom: "8px",
          }}
        >
          <div
            style={{
              maxWidth: "70%",
              backgroundColor: isOwnMessage ? "#0084ff" : "#f0f0f0",
              color: isOwnMessage ? "white" : "#333",
              padding: "8px 12px",
              borderRadius: "18px",
              wordWrap: "break-word",
            }}
          >
            {message.type === "image" && message.fileUrl && (
              <div style={{ marginBottom: message.text ? "8px" : "0" }}>
                <img
                  src={message.fileUrl}
                  alt={message.fileName}
                  style={{
                    maxWidth: "200px",
                    maxHeight: "200px",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                  onClick={() => window.open(message.fileUrl, "_blank")}
                />
              </div>
            )}

            {message.type === "document" && message.fileUrl && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: message.text ? "8px" : "0",
                  cursor: "pointer",
                  padding: "4px",
                  borderRadius: "4px",
                  backgroundColor: isOwnMessage
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.05)",
                }}
                onClick={() => window.open(message.fileUrl, "_blank")}
              >
                <img src={Documents} width="16" height="16" alt="Document" />
                <span style={{ fontSize: "14px" }}>{message.fileName}</span>
              </div>
            )}

            {message.text && (
              <div style={{ fontSize: "14px", lineHeight: "1.4" }}>
                {message.text}
              </div>
            )}

            <div
              style={{
                fontSize: "11px",
                marginTop: "4px",
                opacity: 0.7,
                textAlign: "right",
              }}
            >
              {formatTime(message.timestamp)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "600px",
        width: "400px",
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "16px",
          backgroundColor: "#0084ff",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: "16px" }}>
            Chat with {receiverDisplayName}
          </h3>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "white",
              fontSize: "20px",
              cursor: "pointer",
              padding: "0",
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        )}
      </div>

      {/* Messages Container */}
      <div
        style={{
          flex: 1,
          padding: "16px",
          overflowY: "auto",
          backgroundColor: "#fafafa",
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {loading && messages.length === 0 ? (
          <div
            style={{ textAlign: "center", color: "#65676b", padding: "20px" }}
          >
            Loading messages...
          </div>
        ) : messages.length === 0 ? (
          <div
            style={{ textAlign: "center", color: "#65676b", padding: "20px" }}
          >
            No messages yet. Start the conversation!
          </div>
        ) : (
          messages.map(renderMessage)
        )}

        {isDragOver && (
          <div
            style={{
              position: "absolute",
              top: "60px",
              left: "16px",
              right: "16px",
              bottom: "80px",
              backgroundColor: "rgba(0, 132, 255, 0.1)",
              border: "2px dashed #0084ff",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#0084ff",
              fontSize: "16px",
              fontWeight: "500",
              pointerEvents: "none",
            }}
          >
            Drop file here to send
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Selected File Preview */}
      {selectedFile && (
        <div
          style={{
            padding: "8px 16px",
            backgroundColor: "#e3f2fd",
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img
              src={selectedFile.type.startsWith("image/") ? Camera : Documents}
              width="16"
              height="16"
              alt="File"
            />
            <span style={{ fontSize: "12px", color: "#333" }}>
              {selectedFile.name}
            </span>
          </div>
          <button
            onClick={() => setSelectedFile(null)}
            style={{
              background: "none",
              border: "none",
              color: "#666",
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Input Area */}
      <div
        style={{
          padding: "16px",
          backgroundColor: "white",
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          alignItems: "flex-end",
          gap: "8px",
        }}
      >
        {/* File Upload Buttons */}
        <button
          onClick={() => imageInputRef.current?.click()}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "8px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title="Send Image"
        >
          <img src={Camera} width="20" height="20" alt="Camera" />
        </button>

        <button
          onClick={() => fileInputRef.current?.click()}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "8px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title="Send Document"
        >
          <img src={Documents} width="20" height="20" alt="Documents" />
        </button>

        {/* Message Input */}
        <textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type a message..."
          style={{
            flex: 1,
            border: "1px solid #e0e0e0",
            borderRadius: "20px",
            padding: "8px 12px",
            resize: "none",
            maxHeight: "80px",
            minHeight: "36px",
            outline: "none",
            fontSize: "14px",
          }}
          rows={1}
        />

        {/* Send Button */}
        <button
          onClick={handleSendMessage}
          disabled={(!newMessage.trim() && !selectedFile) || loading}
          style={{
            backgroundColor:
              (!newMessage.trim() && !selectedFile) || loading
                ? "#e0e0e0"
                : "#0084ff",
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: "36px",
            height: "36px",
            cursor:
              (!newMessage.trim() && !selectedFile) || loading
                ? "not-allowed"
                : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "16px",
          }}
        >
          {loading ? "⋯" : "→"}
        </button>

        {/* Hidden File Inputs */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />
      </div>

      {error && (
        <div
          style={{
            padding: "8px 16px",
            backgroundColor: "#ffebee",
            color: "#c62828",
            fontSize: "12px",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default Chat;
